import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Dialog, IconButton, Typography, Tooltip, Button, CircularProgress, Chip, Menu, MenuItem } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import SkipPreviousRoundedIcon from '@mui/icons-material/SkipPreviousRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { useTranslation } from 'react-i18next';
import { useInstatDesign } from '../theme/instatDesign';
import { getMediaUrl } from '../../../utils/mediaUrl';
import api from '../../../api/axiosConfig';
import TelestrationToolbar from './TelestrationToolbar';
import TelestrationTimeline from './TelestrationTimeline';
import AnnotationStage from './AnnotationStage';
import { useTelestrationScene } from './hooks/useTelestrationScene';
import { supportsKeyframes } from './shapes/ShapeRenderer';

const FRAME_STEP = 1 / 30;

function formatTime(t) {
  if (!Number.isFinite(t)) return '0:00.00';
  const m = Math.floor(t / 60);
  const s = t - m * 60;
  return `${m}:${s.toFixed(2).padStart(5, '0')}`;
}

// The DB stores match_date as DATE, but the mysql driver returns it as a
// Date object → JSON.stringify produces an ISO string with a TZ shift that
// can flip the day. We format from the raw YYYY-MM-DD slice to avoid the
// timezone drift.
function formatMatchDate(raw) {
  if (!raw) return '';
  const ymd = String(raw).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return String(raw);
  const [, y, mo, da] = m;
  const d = new Date(Number(y), Number(mo) - 1, Number(da));
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

const TOOL_SHORTCUTS = {
  v: 'select',
  a: 'arrow',
  f: 'curvedArrow',
  l: 'line',
  c: 'circle',
  r: 'rect',
  p: 'pentagon',
  g: 'polygon',
  s: 'spotlight',
  t: 'text',
  j: 'playerToken',
  d: 'playerDuo',
  m: 'playerMove',
  b: 'playerDribble',
};

export default function TelestrationEditor({
  clip,
  open,
  onClose,
  onTagsChange,
  playlist,      // optional: array of clips for prev/next navigation
  onNavigate,    // optional: (newClip) => void — called when the user uses prev/next
}) {
  const d = useInstatDesign();
  const { t } = useTranslation();
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState(null); // 'ok' | 'err'

  // Tag editor state — scoped to clips linked to an informe (consistent with
  // the extension popover rule). Tags are seeded from clip.tags then mutated
  // locally; each change PUTs to the backend.
  const tagsEnabled = !!clip?.informe_id;
  const [currentTags, setCurrentTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const sceneApi = useTelestrationScene();
  const { scene } = sceneApi;

  // Hint shown when an animatable shape is selected (so user understands keyframe UX)
  const selectedObj = scene.objects.find((o) => o.id === scene.selectedId);
  const selectedIsAnimatable = selectedObj && supportsKeyframes(selectedObj.type);
  const selectedKeyframeCount = (selectedObj?.keyframes || []).length;

  // ── Load existing telestration when opening a clip ────────────────
  useEffect(() => {
    if (!clip?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/api/instat/clips/${clip.id}/telestration`);
        if (cancelled) return;
        const loaded = data?.telestration?.scene;
        if (loaded && Array.isArray(loaded.objects)) {
          sceneApi.loadScene({
            objects: loaded.objects,
            defaultDuration: loaded.defaultDuration || 3,
            color: loaded.color || '#FFD60A',
            strokeWidth: loaded.strokeWidth || 3,
            activeTool: 'select',
          });
        } else {
          sceneApi.loadScene({ objects: [], activeTool: 'select' });
        }
      } catch (e) {
        console.warn('[telestration] load failed:', e.message);
        sceneApi.loadScene({ objects: [], activeTool: 'select' });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip?.id]);

  // ── Seed current tags from the clip prop whenever it changes ────────
  useEffect(() => {
    setCurrentTags(Array.isArray(clip?.tags) ? clip.tags : []);
  }, [clip?.id, clip?.tags]);

  // ── Fetch the club's available tags so we can show the "+ Tag" menu ─
  useEffect(() => {
    if (!open || !tagsEnabled) return;
    let cancelled = false;
    api.get('/api/instat-tags')
      .then(({ data }) => { if (!cancelled) setAvailableTags(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setAvailableTags([]); });
    return () => { cancelled = true; };
  }, [open, tagsEnabled]);

  const persistTags = useCallback(async (nextTagIds, nextTagsForUi) => {
    if (!clip?.id || tagsSaving) return;
    setTagsSaving(true);
    // Optimistic update so the UI feels instant.
    setCurrentTags(nextTagsForUi);
    try {
      const { data } = await api.put(`/api/instat/clips/${clip.id}/tags`, { tagIds: nextTagIds });
      const serverTags = Array.isArray(data?.tags) ? data.tags : nextTagsForUi;
      setCurrentTags(serverTags);
      if (onTagsChange) onTagsChange(clip.id, serverTags);
    } catch (e) {
      // Rollback to whatever the server thinks if our PUT failed.
      // eslint-disable-next-line no-console
      console.warn('[telestration] tag update failed:', e?.message);
      setCurrentTags(Array.isArray(clip?.tags) ? clip.tags : []);
    } finally {
      setTagsSaving(false);
    }
  }, [clip?.id, clip?.tags, onTagsChange, tagsSaving]);

  const removeTag = (tagId) => {
    const next = currentTags.filter((t) => t.id !== tagId);
    persistTags(next.map((t) => t.id), next);
  };

  const addTag = (tag) => {
    if (currentTags.some((t) => t.id === tag.id)) return;
    const next = [...currentTags, tag];
    persistTags(next.map((t) => t.id), next);
    setAddMenuAnchor(null);
  };

  const tagsToAdd = useMemo(
    () => availableTags.filter((at) => !currentTags.some((ct) => ct.id === at.id)),
    [availableTags, currentTags],
  );

  // ── Playlist navigation (prev/next) ───────────────────────────────
  // The parent passes the visible clips so we don't accidentally jump to a
  // clip the user can't currently see (e.g. filtered out of the active tag
  // tab). Disabled at boundaries — no wrap-around to avoid surprise loops.
  const playlistIndex = useMemo(() => {
    if (!Array.isArray(playlist) || !clip) return -1;
    return playlist.findIndex((c) => c.id === clip.id);
  }, [playlist, clip]);
  const hasPrev = playlistIndex > 0;
  const hasNext = Array.isArray(playlist) && playlistIndex >= 0 && playlistIndex < playlist.length - 1;
  const goPrev = useCallback(() => {
    if (!hasPrev || !onNavigate) return;
    onNavigate(playlist[playlistIndex - 1]);
  }, [hasPrev, onNavigate, playlist, playlistIndex]);
  const goNext = useCallback(() => {
    if (!hasNext || !onNavigate) return;
    onNavigate(playlist[playlistIndex + 1]);
  }, [hasNext, onNavigate, playlist, playlistIndex]);

  // ── Video sync via rAF — avoids event-timing races with cached video ─
  useEffect(() => {
    if (!open) return;
    let rafId;
    const tick = () => {
      const v = videoRef.current;
      if (v) {
        if (Number.isFinite(v.duration) && v.duration > 0) {
          setDuration((prev) => (prev === v.duration ? prev : v.duration));
        }
        const ct = v.currentTime;
        setCurrentTime((prev) => (prev === ct ? prev : ct));
        const playing = !v.paused && !v.ended;
        setIsPlaying((prev) => (prev === playing ? prev : playing));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [open, clip?.id]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }, []);

  const stepFrame = useCallback((dir) => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.currentTime = Math.max(0, Math.min(duration, v.currentTime + dir * FRAME_STEP));
  }, [duration]);

  const seekTo = useCallback((t) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.max(0, Math.min(duration || 0, t));
  }, [duration]);

  const changeRate = useCallback((rate) => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = rate;
    setPlaybackRate(rate);
  }, []);

  // ── Save telestration ─────────────────────────────────────────────
  const saveTelestration = useCallback(async () => {
    if (!clip?.id || saving) return;
    setSaving(true);
    try {
      await api.put(`/api/instat/clips/${clip.id}/telestration`, {
        scene: {
          version: scene.version,
          objects: scene.objects,
          defaultDuration: scene.defaultDuration,
          color: scene.color,
          strokeWidth: scene.strokeWidth,
        },
      });
      setSaveFlash('ok');
      setTimeout(() => setSaveFlash(null), 2000);
    } catch (e) {
      console.error('[telestration] save failed:', e.message);
      setSaveFlash('err');
      setTimeout(() => setSaveFlash(null), 3000);
    } finally {
      setSaving(false);
    }
  }, [clip?.id, saving, scene]);

  // ── Keyboard shortcuts ────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;

      const key = e.key.toLowerCase();

      // Undo / redo
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) sceneApi.redo();
        else sceneApi.undo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && key === 'y') {
        e.preventDefault();
        sceneApi.redo();
        return;
      }

      // Save (Ctrl+S)
      if ((e.ctrlKey || e.metaKey) && key === 's') {
        e.preventDefault();
        saveTelestration();
        return;
      }

      // Tool shortcuts
      if (!e.ctrlKey && !e.metaKey && TOOL_SHORTCUTS[key]) {
        e.preventDefault();
        sceneApi.setTool(TOOL_SHORTCUTS[key]);
        return;
      }

      if (e.code === 'Space') { e.preventDefault(); togglePlay(); }
      // Shift+Arrow navigates clips; plain Arrow stays as frame step.
      else if (e.shiftKey && e.code === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.shiftKey && e.code === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.code === 'ArrowLeft') { e.preventDefault(); stepFrame(-1); }
      else if (e.code === 'ArrowRight') { e.preventDefault(); stepFrame(1); }
      else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (scene.selectedId) { e.preventDefault(); sceneApi.deleteObject(); }
      }
      else if (e.code === 'Escape') {
        if (scene.selectedId) sceneApi.selectObject(null);
        else if (scene.activeTool !== 'select') sceneApi.setTool('select');
        else onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, togglePlay, stepFrame, onClose, sceneApi, scene.selectedId, scene.activeTool, saveTelestration, goPrev, goNext]);

  if (!clip) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '96vw',
          height: '94vh',
          maxWidth: '96vw',
          maxHeight: '94vh',
          m: 0,
          bgcolor: d.colors.canvas,
          borderRadius: `${d.radius.lg}px`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.2,
          borderBottom: `1px solid ${d.colors.divider}`,
          bgcolor: d.colors.surface,
          flexShrink: 0,
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ ...d.typo.headline }}>
            {clip.title || clip.fname}
          </Typography>
          <Typography noWrap sx={{ ...d.typo.caption }}>
            {clip.match_title}{clip.match_date ? ` · ${formatMatchDate(clip.match_date)}` : ''}
            {clip.quality ? ` · ${clip.quality}p` : ''}
          </Typography>

          {tagsEnabled && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
              {currentTags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={tag.libelle}
                  size="small"
                  onDelete={tagsSaving ? undefined : () => removeTag(tag.id)}
                  sx={{
                    height: 22,
                    fontSize: 11.5,
                    fontWeight: 600,
                    bgcolor: tag.code_couleur,
                    color: '#fff',
                    border: 'none',
                    '& .MuiChip-deleteIcon': {
                      color: 'rgba(255,255,255,0.85)',
                      '&:hover': { color: '#fff' },
                    },
                  }}
                />
              ))}

              <Button
                size="small"
                variant="outlined"
                startIcon={tagsSaving
                  ? <CircularProgress size={12} sx={{ color: d.colors.textSecondary }} />
                  : <AddRoundedIcon sx={{ fontSize: 16 }} />
                }
                disabled={tagsSaving || tagsToAdd.length === 0}
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
                sx={{
                  height: 22,
                  py: 0,
                  px: 1,
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '999px',
                  borderColor: d.colors.divider,
                  color: d.colors.textSecondary,
                  '& .MuiButton-startIcon': { mr: 0.5 },
                }}
              >
                {tagsToAdd.length > 0
                  ? t('instat.tags.addTag')
                  : (availableTags.length === 0
                      ? t('instat.tags.noTagsAvailable')
                      : t('instat.tags.allApplied'))}
              </Button>

              <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                PaperProps={{ sx: { mt: 0.5, minWidth: 180, borderRadius: `${d.radius.md}px` } }}
              >
                {tagsToAdd.map((tag) => (
                  <MenuItem
                    key={tag.id}
                    onClick={() => addTag(tag)}
                    sx={{ gap: 1, py: 0.75 }}
                  >
                    <Box
                      sx={{
                        width: 12, height: 12,
                        borderRadius: '50%',
                        bgcolor: tag.code_couleur,
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}
                    />
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{tag.libelle}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          )}
        </Box>

        <Tooltip title={t('instat.telestration.saveTooltip')}>
          <span>
            <Button
              size="small"
              variant="contained"
              disableElevation
              startIcon={saving
                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                : <SaveRoundedIcon />
              }
              onClick={saveTelestration}
              disabled={saving}
              sx={{
                mr: 1,
                textTransform: 'none',
                bgcolor: saveFlash === 'ok' ? d.colors.success
                  : saveFlash === 'err' ? d.colors.danger
                  : d.accent,
                '&:hover': { bgcolor: saveFlash === 'ok' ? d.colors.success : d.accent },
              }}
            >
              {saveFlash === 'ok'
                ? t('instat.telestration.saved')
                : saveFlash === 'err'
                  ? t('instat.telestration.saveError')
                  : t('instat.telestration.save')}
            </Button>
          </span>
        </Tooltip>

        <IconButton onClick={onClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            bgcolor: '#000',
          }}
        >
          <Box
            sx={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <video
              ref={videoRef}
              key={clip.id}
              src={getMediaUrl('instat_clips', clip.filename)}
              autoPlay
              muted
              playsInline
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                display: 'block',
                outline: 'none',
              }}
            />
            <AnnotationStage
              videoRef={videoRef}
              scene={scene}
              sceneApi={sceneApi}
              currentTime={currentTime}
              duration={duration}
            />

            {selectedIsAnimatable && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  px: 1.5,
                  py: 0.6,
                  borderRadius: '999px',
                  bgcolor: 'rgba(0,0,0,0.72)',
                  color: '#fff',
                  fontSize: 11.5,
                  fontWeight: 500,
                  pointerEvents: 'none',
                  letterSpacing: '-0.01em',
                }}
              >
                {t('instat.telestration.keyframeHint', { count: selectedKeyframeCount })}
              </Box>
            )}

            {/* Playlist nav arrows — only rendered if a playlist is provided.
                Disabled at boundaries; counter shows current position. */}
            {Array.isArray(playlist) && playlist.length > 1 && playlistIndex >= 0 && (
              <>
                <Tooltip title={t('instat.telestration.prevClip')} placement="right">
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}>
                    <IconButton
                      onClick={goPrev}
                      disabled={!hasPrev}
                      size="large"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        backdropFilter: 'blur(4px)',
                        opacity: hasPrev ? 1 : 0.3,
                        transition: 'opacity 120ms, transform 120ms',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.72)', transform: 'scale(1.05)' },
                        '&.Mui-disabled': { color: '#fff' },
                      }}
                    >
                      <ArrowBackIosNewRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={t('instat.telestration.nextClip')} placement="left">
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5 }}>
                    <IconButton
                      onClick={goNext}
                      disabled={!hasNext}
                      size="large"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        backdropFilter: 'blur(4px)',
                        opacity: hasNext ? 1 : 0.3,
                        transition: 'opacity 120ms, transform 120ms',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.72)', transform: 'scale(1.05)' },
                        '&.Mui-disabled': { color: '#fff' },
                      }}
                    >
                      <ArrowForwardIosRoundedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    right: 12,
                    px: 1.25,
                    py: 0.4,
                    borderRadius: '999px',
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    pointerEvents: 'none',
                    backdropFilter: 'blur(4px)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {playlistIndex + 1} / {playlist.length}
                </Box>
              </>
            )}
          </Box>

          {/* Transport bar */}
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              bgcolor: '#111',
              color: '#fff',
            }}
          >
            <Tooltip title={t('instat.telestration.prevFrame')}>
              <IconButton size="small" onClick={() => stepFrame(-1)} sx={{ color: '#fff' }}>
                <SkipPreviousRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isPlaying ? t('instat.telestration.pause') : t('instat.telestration.play')}>
              <IconButton size="small" onClick={togglePlay} sx={{ color: '#fff' }}>
                {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('instat.telestration.nextFrame')}>
              <IconButton size="small" onClick={() => stepFrame(1)} sx={{ color: '#fff' }}>
                <SkipNextRoundedIcon />
              </IconButton>
            </Tooltip>

            <Typography sx={{ ...d.typo.subhead, ...d.typo.tabular, color: '#fff', mx: 1, minWidth: 120 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>

            <Box sx={{ flex: 1 }}>
              <input
                type="range"
                min={0}
                max={duration || 0}
                step={0.01}
                value={currentTime}
                onChange={(e) => seekTo(parseFloat(e.target.value))}
                style={{ width: '100%' }}
              />
            </Box>

            {/* Playback speed selector */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                ml: 1,
                p: 0.25,
                borderRadius: `${d.radius.sm}px`,
                bgcolor: 'rgba(255,255,255,0.08)',
              }}
            >
              {[0.25, 0.5, 0.75, 1, 1.5, 2].map((r) => (
                <Box
                  key={r}
                  onClick={() => changeRate(r)}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    px: 0.8,
                    py: 0.25,
                    borderRadius: `${d.radius.xs}px`,
                    cursor: 'pointer',
                    color: playbackRate === r ? '#000' : '#fff',
                    bgcolor: playbackRate === r ? '#fff' : 'transparent',
                    fontVariantNumeric: 'tabular-nums',
                    '&:hover': {
                      bgcolor: playbackRate === r ? '#fff' : 'rgba(255,255,255,0.15)',
                    },
                  }}
                >
                  {r}x
                </Box>
              ))}
            </Box>
          </Box>

          <TelestrationTimeline
            duration={duration}
            currentTime={currentTime}
            onSeek={seekTo}
            scene={scene}
            sceneApi={sceneApi}
          />
        </Box>

        <TelestrationToolbar scene={scene} sceneApi={sceneApi} />
      </Box>
    </Dialog>
  );
}
